import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Label,
  Button,
} from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "formik"
import Breadcrumbs from "components/Common/Breadcrumb"

import { addUserSchemas, editUserSchemas } from "components/Common/validation"
import { useLocation, useNavigate } from "react-router"
import { useDispatch } from "react-redux"
import {
  addAdminUser,
  getAdminUserDetails,
  getAllBranchs,
  getAllRoles,
  updateAdminUser,
} from "store/actions/usersAction"
import { useSelector } from "react-redux"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import Select from "react-select"
import BackButton from "components/Shared/BackButton"
import { allOption } from "components/Common/constants"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import Switch from "components/Shared/Switch"

const initialValues = {
  firstName: "",
  lastName: "",
  username: "",
  password: "",
  email: "",
  mobile: "",
  roles: "",
  locations: [],
  allowExports: false,
}

const AddUser = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } ${"Add User | Namq"}`

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const [formValues, setFormValues] = useState(initialValues)
  const [rolesData, setRolesData] = useState([])
  const [locationsData, setLocationsData] = useState([])
  const [selectedValues, setSelectedValues] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const {
    loading,
    error,
    isLoading,
    isUpdating,
    message,
    actionType,
    rolesList,
    branchsList,
    userDetails,
  } = useSelector(state => state.Users)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      6,
      "usersPermission_3",
      accountType
    )
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    const getUsersDetails = () => {
      const payload = {
        id,
      }
      dispatch(getAdminUserDetails(payload))
    }
    if (mode === "edit") {
      getUsersDetails()
    }
  }, [])

  // GET ROLES AND LOCATIONS
  useEffect(() => {
    const fetchAllRoles = () => {
      dispatch(getAllRoles())
    }
    const fetchAllBranchs = () => {
      dispatch(getAllBranchs())
    }
    fetchAllRoles()
    fetchAllBranchs()
  }, [])

  // SET USER DETAILS
  useEffect(() => {
    if (userDetails && mode === "edit") {
      const {
        firstName,
        lastName,
        email,
        phoneNumber,
        role,
        locations,
        username,
        allowExports,
      } = userDetails

      // const locationIds = locations?.map(location => location._id)
      const locationsOptions = locations?.map(location => ({
        label: language === "sa" ? location.nameAr : location.name,
        value: location._id,
      }))

      const updatedRole = {
        label: role?.name,
        value: role?._id,
      }

      setFormValues(prevVal => ({
        ...prevVal,
        firstName,
        lastName,
        username,
        email,
        mobile: phoneNumber,
        roles: updatedRole,
        locations: locationsOptions,
        allowExports,
      }))
    }
  }, [userDetails])

  // SET ROLES
  useEffect(() => {
    setRolesData(rolesList)
  }, [rolesList])

  // SET BRANCHS
  useEffect(() => {
    setLocationsData(branchsList)
  }, [branchsList])

  const handleOnSubmit = values => {
    const {
      firstName,
      lastName,
      username,
      password,
      email,
      mobile,
      roles,
      locations,
      allowExports,
    } = values

    const locationValues = locations.map(location => location.value)
    const updatedRole = roles.value
    const payload = {
      firstName,
      lastName,
      // username,
      email,
      phoneNumber: mobile,
      password,
      role: updatedRole,
      locations: locationValues,
      allowExports,
    }

    if (mode === "add") {
      dispatch(addAdminUser({ ...payload, navigate }))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const payload = {
        id,
        firstName,
        lastName,
        // username,
        email,
        phoneNumber: mobile,
        role: updatedRole,
        locations: locationValues,
        allowExports,
      }
      dispatch(updateAdminUser(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const branchListOptions = branchsList?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const options = isAllSelected
    ? branchListOptions
    : [allOption, ...branchListOptions]

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content edit_user_page">
      <Container fluid={true}>
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("User")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={
                    mode === "add" ? addUserSchemas : editUserSchemas
                  }
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <Row className="mb-4">
                        <Col md={6}>
                          <div className="form_field">
                            <Label
                              htmlFor="horizontal-firstname-Input"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("First Name")}
                            </Label>
                            <Field
                              name="firstName"
                              type="text"
                              id="horizontal-firstname-Input"
                              placeholder={t("First Name")}
                              className={`form-control ${
                                touched.firstName && errors.firstName
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="firstName" />
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="form_field">
                            <Label
                              htmlFor="horizontal-firstname-Input"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("Last Name")}
                            </Label>
                            <Field
                              name="lastName"
                              type="text"
                              className={`form-control ${
                                touched.lastName && errors.lastName
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="horizontal-firstname-Input"
                              placeholder={t("Last Name")}
                            />
                            <TextError name="lastName" />
                          </div>
                        </Col>

                        <Col md={6}>
                          <div className="">
                            <Label
                              htmlFor="horizontal-password-Input"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("Roles")}
                            </Label>
                            <div className="mt-1">
                              <Select
                                value={values.roles}
                                onChange={item => {
                                  setFieldValue("roles", item)
                                }}
                                options={rolesData?.map(item => ({
                                  label: item.name,
                                  value: item._id,
                                }))}
                                className={`select2-selection ${
                                  touched.roles && errors.roles
                                    ? "danger_color_border"
                                    : ""
                                } `}
                                placeholder={t("Roles")}
                              />
                            </div>

                            <TextError name="roles" />
                          </div>
                        </Col>

                        {/* {mode === "add" && (
                          <Col sm={4}>
                            <div className="form_field">
                              <Label
                                htmlFor="horizontal-firstname-Input"
                                className="col col-form-label"
                              >
                                <span className="text-danger">*</span>{" "}
                                {t("Username")}
                              </Label>
                              <Field
                                name="username"
                                type="text"
                                className={`form-control ${
                                  touched.username && errors.username
                                    ? "is-invalid"
                                    : ""
                                }`}
                                id="horizontal-username-Input"
                                placeholder={t("Username")}
                              />
                              <TextError
                                name="username"
                                
                              />
                            </div>
                          </Col>
                        )} */}
                        {mode === "add" && (
                          <Col sm={6}>
                            <div className="form_field ">
                              <Label
                                htmlFor="horizontal-password-Input"
                                className="col col-form-label"
                              >
                                <span className="text-danger">*</span>{" "}
                                {t("Password")}
                              </Label>
                              <div className="position-relative">
                                <Field
                                  type={showPassword ? "text" : "password"}
                                  name="password"
                                  className={`form-control ${
                                    touched.password && errors.password
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  id="horizontal-password-Input"
                                  autoComplete="off"
                                  placeholder={t("Password")}
                                />
                                <i
                                  onClick={() => setShowPassword(!showPassword)}
                                  className={`bx bx-${
                                    showPassword ? "show-alt" : "hide"
                                  } common_eye`}
                                ></i>
                              </div>
                              <TextError name="password" />
                            </div>
                          </Col>
                        )}

                        <Col sm={6}>
                          <div className="form_field">
                            <Label
                              htmlFor="horizontal-email-Input"
                              className="col col-form-label"
                            >
                              <span className="text-danger">*</span>{" "}
                              {t("Email")}
                            </Label>
                            <Field
                              type="email"
                              name="email"
                              className={`form-control ${
                                touched.email && errors.email
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="horizontal-email-Input"
                              placeholder={t("Email")}
                            />
                            <TextError name="email" />
                          </div>
                        </Col>

                        <Col sm={6}>
                          <div className="form_field">
                            <Label
                              htmlFor="horizontal-email-Input"
                              className="col col-form-label"
                            >
                              {t("Phone Number")}
                            </Label>
                            <Field
                              type="text"
                              name="mobile"
                              className={`form-control ${
                                touched.mobile && errors.mobile
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="horizontal-mobile-Input"
                              placeholder={t("Phone Number")}
                            />
                            <TextError name="mobile" />
                          </div>
                        </Col>

                        {values.roles.label !== "Super Admin" && (
                          <Col md={6}>
                            <div className="">
                              <Label
                                htmlFor="horizontal-password-Input"
                                className="col col-form-label"
                                style={{ marginBottom: 7 }}
                              >
                                {t("Branches")}
                              </Label>

                              <Select
                                value={values.locations}
                                isMulti={true}
                                onChange={item => {
                                  if (
                                    item?.find(option => option.value === "*")
                                  ) {
                                    setFieldValue(
                                      "locations",
                                      branchListOptions
                                    )
                                    setIsAllSelected(true)
                                  } else {
                                    setFieldValue("locations", item)
                                    setIsAllSelected(
                                      item.length === branchListOptions.length
                                    )
                                  }
                                }}
                                options={options}
                                className="select2-selection"
                                closeMenuOnSelect={false}
                                placeholder={t("Branches")}
                              />
                              <TextError name="locations" />
                            </div>
                          </Col>
                        )}

                        <Col sm={6}>
                          <div className="form_field">
                            <Label
                              htmlFor="allowExports"
                              className="col col-form-label"
                            >
                              {t("Allow Exports")}
                            </Label>

                            <Switch
                              onClickHandler={e => {
                                setFieldValue(
                                  "allowExports",
                                  !values.allowExports
                                )
                              }}
                              isChecked={values.allowExports}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="justify-content-end my-5">
                        <Col sm={12}>
                          <div className="d-flex justify-content-end">
                            <Button
                              type="button"
                              color="secondary"
                              onClick={() => navigate(-1)}
                            >
                              {t("Cancel")}
                            </Button>
                            <Button
                              className="mx-2"
                              type="submit"
                              color="success"
                              disabled={isLoading}
                            >
                              {mode === "add" ? t("Add") : t("Save")}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default AddUser
